import React, { useState, useMemo, useRef, useEffect } from "react";
import _ from "lodash";
import {
  BreadcrumbComponent,
  PaginationComponent,
  SearchInputComponent,
  SelectInputComponent,
} from "../../../common/components";
import { routes } from "../../../constants/routes";
import DataTable from "../../campaign/campiagn-view/campaign-view-details/campaign-view-details-view/campaign-view-details-view-activities-info/task-table/task-table";
import {
  getAgentCalls,
  getAgentsList,
  getAIAgentsData,
  getCallDetails,
  getToolsForCall,
} from "../../../api/ai-call-monitoring";
import viewCampaignIcon from "../../../assets/icons/left-menu-icons/campaigns/view-campaigns-icon.svg";
import { useSetLoading } from "../../../common/hooks/appearance";
import "./aiCallMonitoring.scss";
import {
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
} from "@mui/material";
import IconButton from "@mui/material/IconButton";
import CloseIcon from "@mui/icons-material/Close";
import ArrowDropDownIcon from "@mui/icons-material/ArrowDropDown";
import ArrowDropUpIcon from "@mui/icons-material/ArrowDropUp";
import CustomRadioGroup from "../../../common/components/radio-button-component/radio-button-component";
import DateFilter from "./date-filter";

const AICallMonitoring = () => {
  const [isLoading, setIsLoading] = React.useState(false);
  const [agentData, setAgentData] = React.useState([]);
  const [agentList, setAgentList] = React.useState<
    { label: string; value: string }[]
  >([]);
  const isSearchTriggered = useRef(false);
  const [searchText, setSearchText] = React.useState("");
  const [agentFilter, setAgentFilter] = React.useState("all");
  const [statusFilter, setStatusFilter] = React.useState("all");
  const [dateFilterType, setDateFilterType] = useState<
    "last30days" | "daterange"
  >("last30days");
  const getLast30DaysRange = () => {
    const end = new Date();
    const start = new Date();

    start.setDate(end.getDate() - 30);

    return { start, end };
  };

  const [dateFilter, setDateFilter] = useState<{
    start: Date | null;
    end: Date | null;
  }>(getLast30DaysRange());
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);
  const [selectedAgent, setSelectedAgent] = React.useState({
    agentId: null,
    agentName: "",
  });
  const [agentListPage, setAgentListPage] = React.useState(1);
  const [totalAgentRecords, setTotalAgentRecords] = React.useState(0);
  const [callsData, setCallsData] = React.useState([]);
  const [callsListPage, setCallsListPage] = React.useState(1);
  const [totalCallsRecords, setTotalCallsRecords] = React.useState(0);
  const [selectedCallId, setSelectedCallId] = React.useState(null);
  const [callDetails, setCallDetails] = React.useState<any>({});
  const [currentView, setCurrentView] = React.useState<"agents" | "calls">(
    "agents",
  );
  const [isCallDetailsModalOpen, setIsCallDetailsModalOpen] =
    React.useState(false);

  const [selectedCallTools, setSelectedCallTools] = React.useState<{
    callId: string | null;
    agentId: string | null;
  }>({
    callId: null,
    agentId: null,
  });

  const [callTools, setCallTools] = useState<any[]>([]);
  const [isCallToolsModalOpen, setIsCallToolsModalOpen] = useState(false);
  // const filterRef = useRef<HTMLDivElement>(null);

  const formatName = (name: string) => {
    if (!name) return "-";

    return name
      .replaceAll("_", " ")
      .replace(/\b\w/g, (char) => char.toUpperCase());
  };

  const crumbMapping = [
    { label: "Settings", link: "#" },
    {
      label: "AI Call Monitoring",
      link: routes.AI_CALL_MONITORING,
      handleClick: () => {
        setCurrentView("agents");
      },
    },
    ...(currentView === "calls"
      ? [
          {
            label: formatName(selectedAgent.agentName) || "Calls",
            link: "#",
          },
        ]
      : []),
  ];

  const formatDuration = (ms: number) => {
    if (ms == null) return "-";

    if (ms < 1000) {
      return `${ms}ms`;
    }

    const seconds = Math.floor(ms / 1000);

    const hrs = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    const secs = seconds % 60;

    if (hrs > 0) {
      return `${hrs}h ${mins}m ${secs}s`;
    }

    if (mins > 0) {
      return `${mins}m ${secs}s`;
    }

    return `${secs}s`;
  };

  const agentColumns = [
    {
      key: "agentName",
      label: "Agent Name",
      render: (row: any) => formatName(row.agentName),
    },
    {
      key: "status",
      label: "Status",
      render: (row: any) => (
        <span className={row.status ? "active-status" : "inactive-status"}>
          {row.status ? "Active" : "Inactive"}
        </span>
      ),
    },
    {
      key: "version",
      label: "Version",
      render: (row: any) => row.version,
    },
    {
      key: "totalCalls",
      label: "Total Calls",
      render: (row: any) => row.totalCalls,
    },
    {
      key: "callDuration",
      label: "Call Duration(Total)",
      render: (row: any) => formatDuration(row.totalDuration),
    },
    {
      key: "toolsExecuted",
      label: "Tools Executed",
      render: (row: any) => row.totalTools,
    },
    // {
    //   key: "sucessTools",
    //   label: "Success Tools",
    //   render: (row: any) => row.totalToolsSuccess,
    // },
    // {
    //   key: "failedTools",
    //   label: "Failed Tools",
    //   render: (row: any) => row.totalToolsFailed,
    // },
    {
      key: "view",
      label: "View Calls",
      render: (row: any) => (
        <div className="icon-cell">
          <img
            src={viewCampaignIcon}
            alt="View Calls"
            width={20}
            height={20}
            className="clickable-icon"
            onClick={() => {
              setSelectedAgent({
                agentId: row.agentId,
                agentName: row.agentName,
              });

              setCurrentView("calls");
            }}
          />
        </div>
      ),
    },
  ];

  const callColumns = [
    {
      key: "callId",
      label: "Call ID",
      render: (row: any) => row.callId,
    },
    {
      key: "phoneNumber",
      label: "Phone Number",
      render: (row: any) => row.phoneNumber,
    },
    // {
    //   key: "callDirection",
    //   label: "Call Direction",
    //   render: (row: any) => row.callDirection,
    // },
    {
      key: "callDuration",
      label: "Call Duration",
      render: (row: any) => formatDuration(row.callDurationMs),
    },
    {
      label: "Call Status",
      key: "callStatus",
      render: (row: any) => {
        const status = row.status?.toLowerCase();

        return (
          <span
            className={`table-status-badge ${
              status === "completed" || status === "success"
                ? "success"
                : status === "failed"
                  ? "failed"
                  : "default"
            }`}
          >
            {row.status}
          </span>
        );
      },
    },
    {
      label: "Tools Executed",
      key: "toolsExecuted",
      render: (row: any) => row.numberOfToolCalls,
    },
    {
      label: "Success Tools",
      key: "success",
      render: (row: any) => row.succeededTotal,
    },
    {
      label: "Failed Tools",
      key: "failed",
      render: (row: any) => row.failedTotal,
    },
    {
      label: "Failed Reason",
      key: "Failed Reason",
      render: (row: any) =>
        typeof row.failureReasons === "string"
          ? row.failureReasons
          : row.failureReasons?.submit_call_history || "-",
    },
    {
      key: "view",
      label: "View Tools",
      render: (row: any) => (
        <div className="icon-cell">
          <img
            src={viewCampaignIcon}
            alt="View Tools"
            width={20}
            height={20}
            onClick={(e) => {
              e.stopPropagation();
              setIsCallToolsModalOpen(true);
              setSelectedCallTools({
                callId: row.callId,
                agentId: row?.agent?.agentId || selectedAgent.agentId,
              });
            }}
          />
        </div>
      ),
    },
  ];

  const toolColumns = [
    {
      key: "toolName",
      label: "Tool Name",
      render: (row: any) => row.toolName,
    },
    {
      key: "toolKind",
      label: "Type",
      render: (row: any) => row.toolKind,
    },
    {
      key: "outcome",
      label: "Status",
      render: (row: any) => (
        <span
          className={`table-status-badge ${
            row.outcome === "succeeded"
              ? "success"
              : row.outcome === "failed"
                ? "failed"
                : "default"
          }`}
        >
          {row.outcome}
        </span>
      ),
    },
    {
      key: "durationMs",
      label: "Duration",
      render: (row: any) => formatDuration(row.durationMs),
    },
    {
      key: "errorCode",
      label: "Error Code",
      render: (row: any) => row.errorCode || "-",
    },
    {
      key: "errorMessage",
      label: "Reason",
      render: (row: any) => row.errorMessage || "-",
    },
  ];

  const fetchAgentData = async () => {
    try {
      setIsLoading(true);

      const params: any = {
        page: agentListPage,
        limit: 15,
        statusFilter,
        agentFilter,
      };

      params.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      if (searchText && isSearchTriggered.current) {
        params.searchValue = searchText;
      }

      if (dateFilter && dateFilter.start && dateFilter.end) {
        params.fromDate = new Date(dateFilter.start).toISOString();
        params.toDate = new Date(dateFilter.end).toISOString();
      }

      const response = await getAIAgentsData(params);

      setAgentData(response.data.data.data);
      setTotalAgentRecords(response.data.data.pagination.totalCount);
    } catch (error) {
      console.error("Error fetching AI agents data:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAgentList = async () => {
    try {
      setIsLoading(true);

      const response = await getAgentsList();

      const agentOptions = response.data.data.data.map((agent: any) => ({
        label: formatName(agent),
        value: agent,
      }));

      setAgentList([{ label: "All", value: "all" }, ...agentOptions]);
    } catch (error) {
      console.error("Error fetching AI agents list:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchAgentCalls = async (agentId: string) => {
    try {
      setIsLoading(true);
      const params: any = {
        page: callsListPage,
        limit: 15,
      };

      if (dateFilter && dateFilter.start && dateFilter.end) {
        params.fromDate = new Date(dateFilter.start).toISOString();
        params.toDate = new Date(dateFilter.end).toISOString();
      }

      const response = await getAgentCalls(agentId, params);

      setCallsData(response.data?.data?.data);
      setTotalCallsRecords(response.data?.data?.pagination?.totalCount);
    } catch (error) {
      console.error("Error fetching agent calls:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchCallDetails = async (agentId: string, callId: string) => {
    try {
      setIsLoading(true);

      const response = await getCallDetails(agentId, callId);

      setCallDetails(response?.data?.data?.data);
    } catch (error) {
      console.error("Error fetching call details:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const fetchToolsForCall = async (agentId: string, callId: string) => {
    try {
      setIsLoading(true);

      const response = await getToolsForCall(agentId, callId);
      console.log(response.data?.data?.data);
      setCallTools(response.data?.data?.data?.tools || []);
    } catch (error) {
      console.error("Error fetching tools for call:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSearch = _.debounce((text: string) => {
    isSearchTriggered.current = true;
    setAgentListPage(1);
    setSearchText(text);
  }, 500);

  React.useEffect(() => {
    if (
      dateFilterType === "daterange" &&
      (!dateFilter.start || !dateFilter.end)
    ) {
      return;
    }

    fetchAgentData();
  }, [searchText, statusFilter, agentFilter, agentListPage, dateFilter]);

  React.useEffect(() => {
    fetchAgentList();
  }, []);

  React.useEffect(() => {
    if (selectedAgent.agentId) {
      fetchAgentCalls(selectedAgent.agentId);
    }
  }, [selectedAgent, callsListPage]);

  useEffect(() => {
    if (selectedCallId && selectedAgent.agentId) {
      fetchCallDetails(selectedAgent.agentId, selectedCallId);
    }
  }, [selectedCallId, selectedAgent.agentId]);

  useEffect(() => {
    if (selectedCallTools.callId && selectedCallTools.agentId) {
      fetchToolsForCall(selectedCallTools.agentId, selectedCallTools.callId);
    }
  }, [selectedCallTools]);

  const showLoading = useMemo(() => isLoading, [isLoading]);

  useSetLoading(showLoading);

  return (
    <div className="ai-call-monitoring-container">
      <BreadcrumbComponent breadcrumbLink={crumbMapping} />

      {currentView === "agents" && (
        <>
          <div className="filter-section">
            <div className="filter-options">
              <div className="filter-option-dropdown">
                <SelectInputComponent
                  options={agentList}
                  value={agentFilter}
                  customRenderValue={(selected: any) => {
                    const agent = agentList.find(
                      (item) => item.value === selected,
                    );

                    return (
                      <div className="ellipsis-text">
                        Agent Name: {agent?.label || "All"}
                      </div>
                    );
                  }}
                  onChange={(value: string) => setAgentFilter(value)}
                />
              </div>

              <div className="filter-option-dropdown">
                <SelectInputComponent
                  options={[
                    { label: "All", value: "all" },
                    { label: "Active", value: "active" },
                    { label: "Inactive", value: "inactive" },
                  ]}
                  value={statusFilter}
                  customRenderValue={(selected: any) => {
                    const labels: any = {
                      all: "All",
                      active: "Active",
                      inactive: "Inactive",
                    };

                    return (
                      <div className="ellipsis-text">
                        Status: {labels[selected]}
                      </div>
                    );
                  }}
                  onChange={(value: string) => setStatusFilter(value)}
                />
              </div>

              <div className="filter-option-search">
                <SearchInputComponent
                  placeholder="Search by Agent Name or Agent ID"
                  onSearch={handleSearch}
                />
              </div>

              <div className="filter-option-date">
                <p>Date</p>

                <div
                  // ref={filterRef}
                  className="filter-value"
                  onClick={() => setIsFilterOpen(!isFilterOpen)}
                >
                  {dateFilterType === "last30days"
                    ? "Last 30 days"
                    : dateFilter.start && dateFilter.end
                      ? `${dateFilter.start.toLocaleDateString()} - ${dateFilter.end.toLocaleDateString()}`
                      : "Date range"}

                  {isFilterOpen ? <ArrowDropUpIcon /> : <ArrowDropDownIcon />}

                  {isFilterOpen && (
                    <div
                      className="filter-dropdown"
                      onClick={(e) => e.stopPropagation()}
                    >
                      <div className="drawer-content">
                        <CustomRadioGroup
                          name="dateFilter"
                          value={dateFilterType}
                          options={[
                            {
                              label: "Last 30 days",
                              value: "last30days",
                            },
                            {
                              label: "Date range",
                              value: "daterange",
                            },
                          ]}
                          onChange={(e) => {
                            const value = e.target.value as
                              | "last30days"
                              | "daterange";

                            setDateFilterType(value);

                            if (value === "last30days") {
                              setDateFilter(getLast30DaysRange());
                            } else {
                              setDateFilter({
                                start: null,
                                end: null,
                              });
                            }
                          }}
                        />

                        {dateFilterType === "daterange" && (
                          <div className="date-range-container">
                            <DateFilter
                              startDate={dateFilter.start}
                              endDate={dateFilter.end}
                              onDateRangeChange={(start, end) => {
                                setDateFilter({
                                  start: start ? new Date(start) : null,
                                  end: end ? new Date(end) : null,
                                });
                              }}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="filter-buttons">
              <button
                className="button clear-button"
                onClick={() => {
                  setSearchText("");
                  setStatusFilter("all");
                  setAgentFilter("all");
                  setDateFilterType("last30days");
                  setDateFilter({
                    start: null,
                    end: null,
                  });
                  setIsFilterOpen(false);
                  setAgentListPage(1);
                }}
              >
                Reset
              </button>
            </div>
          </div>

          <div className="ai-agents-table">
            <DataTable columns={agentColumns} data={agentData} />
          </div>

          {totalAgentRecords > 0 && (
            <div className="pagination-container">
              <PaginationComponent
                totalRecords={totalAgentRecords}
                pageSize={15}
                currentPage={agentListPage}
                onPageChange={(event, value) => {
                  setAgentListPage(value);
                }}
              />
            </div>
          )}
        </>
      )}

      {currentView === "calls" && (
        <div className="ai-agent-details">
          <div className="calls-table">
            <DataTable
              columns={callColumns}
              data={callsData}
              onRowClick={(row: any) => {
                setSelectedCallId(row.callId);
                setIsCallDetailsModalOpen(true);
              }}
            />
          </div>
          {totalCallsRecords > 0 && (
            <div className="pagination-container">
              <PaginationComponent
                totalRecords={totalCallsRecords}
                pageSize={15}
                currentPage={callsListPage}
                onPageChange={(event, value) => {
                  setCallsListPage(value);
                }}
              />
            </div>
          )}
        </div>
      )}

      <Dialog
        open={isCallDetailsModalOpen}
        maxWidth="md"
        fullWidth
        className="ai-call-details-dialog"
      >
        <DialogTitle className="call-details-header">
          Call Details - {selectedCallId}
          <IconButton
            aria-label="close"
            onClick={() => {
              setIsCallDetailsModalOpen(false);
              setSelectedCallId(null);
              setCallDetails({});
            }}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent dividers className="call-details-content">
          <div className="details-grid">
            <div className="detail-card">
              <div className="label">Agent Name</div>
              <div className="value">
                {callDetails?.agent?.agentName || "-"}
              </div>
            </div>

            <div className="detail-card">
              <div className="label">Call Duration</div>
              <div className="value">
                {formatDuration(callDetails?.callDurationMs)}
              </div>
            </div>

            <div className="detail-card status">
              <div className="label">Call Status</div>

              <div
                className={`status-badge ${
                  callDetails?.status?.toLowerCase() || ""
                }`}
              >
                {callDetails?.status || "-"}
              </div>
            </div>

            <div className="detail-card">
              <div className="label">Tools Executed</div>
              <div className="value">{callDetails?.numberOfToolCalls || 0}</div>
            </div>

            <div className="detail-card success">
              <div className="label">Success Tools</div>
              <div className="value">{callDetails?.succeededTotal || 0}</div>
            </div>

            <div className="detail-card failed">
              <div className="label">Failed Tools</div>
              <div className="value">{callDetails?.failedTotal || 0}</div>
            </div>
          </div>

          <div className="transcript-section">
            <div className="transcript-title">Transcript</div>

            <div className="call-transcript">
              {callDetails?.transcript?.turns?.length > 0
                ? callDetails.transcript.turns.map(
                    (turn: any, index: number) => (
                      <div key={index}>
                        {turn.Assistant && (
                          <div>Assistant: {turn.Assistant}</div>
                        )}
                        {turn.User && <div>User: {turn.User}</div>}
                      </div>
                    ),
                  )
                : "No transcript available"}
            </div>
          </div>
        </DialogContent>

        <DialogActions className="call-details-actions">
          <button
            className="cancel-btn"
            onClick={() => {
              setIsCallDetailsModalOpen(false);
              setSelectedCallId(null);
              setCallDetails({});
            }}
          >
            Close
          </button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={isCallToolsModalOpen}
        maxWidth="md"
        fullWidth
        className="call-tools-dialog"
      >
        <DialogTitle>
          Tool Execution Details
          <IconButton
            aria-label="close"
            onClick={() => {
              setIsCallToolsModalOpen(false);
              setSelectedCallId(null);
              setCallDetails({});
            }}
            sx={{
              position: "absolute",
              right: 12,
              top: 12,
              color: (theme) => theme.palette.grey[500],
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers className="call-tools-content">
          <DataTable columns={toolColumns} data={callTools} />
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AICallMonitoring;
